import { AppRoutes } from "@/routes/config";
import { RouteObject } from "react-router-dom";

import { LoginLayout } from "@/components/layout/LoginLayout";
import { MaintenancePage } from "@/features/maintenance/pages/Maintenance";
import ConfirmEmail from "@/pages/auth/ConfirmEmail";
import Login from "@/pages/auth/Login";
import Logout from "@/pages/auth/Logout";
import Recover from "@/pages/auth/Recover";
import RecoverComplete from "@/pages/auth/RecoverComplete";
import Register from "@/pages/auth/Register";
import RegisterComplete from "@/pages/auth/RegisterComplete";
import ResetPassword from "@/pages/auth/ResetPassword";
import ResetPasswordComplete from "@/pages/auth/ResetPasswordComplete";
import PageNotFound from "@/pages/PageNotFound";
import ServerErrorPage from "@/pages/ServerErrorPage";
import { MatchLayoutWithAuth } from "@/components/layout/MatchLayout/MatchLayout";
import { AppRouteNames } from "@/routes/constants";
import Dashboard from "@/pages/dashboard/Dashboard";
import CompanyList from "@/pages/company/CompanyList";
import TalentList from "@/pages/talent/TalentList";
import { CompanyCreatePage } from "@/pages/company/CompanyCreate";
import { CompanyEditPage } from "@/pages/company/CompanyEdit";
import CompanyDetailPage from "@/pages/company/CompanyDetail";
import InformationList from "@/pages/information/InformationList";
import InformationDetail from "@/pages/information/InformationDetail";
import { TalentCreatePage } from "@/pages/talent/TalentCreate";
import CompanyImport from "@/pages/company/CompanyImport";
import { CompanyLayout } from "@/features/company/layout/CompanyLayout";
import { TalentLayout } from "@/features/talent/layout/TalentLayout";
import TalentDetailPage from "@/pages/talent/TalentDetail";
import { TalentEditPage } from "@/pages/talent/TalentEdit";
import UserSettingChangeEmail from "@/pages/user-setting/UserSettingChangeEmail";
import UserSettingDetail from "@/pages/user-setting/UserSettingDetail";
import UserSettingEdit from "@/pages/user-setting/UserSettingEdit";
import { UserSettingLayout } from "@/features/user-setting/layout/UserSettingLayout";
import { MatchLayout } from "@/features/match/layout/MatchLayout";
import Match from "@/pages/match/Match";
import MatchEdit from "@/pages/match/MatchEdit";
import { JobCreatePage } from "@/pages/job/JobCreate";
import { JobLayout } from "@/features/job/layout/JobLayout";
import JobList from "@/pages/job/JobList";
import { JobEditPage } from "@/pages/job/JobEdit";
import JobDetailPage from "@/pages/job/jobDetail";
import TalentImport from "@/pages/talent/TalentImport";
import { CaseLayout } from "@/features/case/layouts/CaseLayout";
import { CaseCreate } from "@/pages/case/CaseCreate";
import { CaseList } from "@/pages/case/CaseList";
import { CaseEdit } from "@/pages/case/CaseEdit";
import ExamStepsList from "@/pages/exam-steps/ExamStepsList";
import ExamStepDetail from "@/pages/exam-steps/ExamStepDetail";
import LearningProgress from "@/pages/learning-progress/LearningProgressList";
import LearningProgressDetail from "@/pages/learning-progress/LearningProgressDetail";
import LearningProgressLearnerDetail from "@/pages/learning-progress/LearningProgressLearnerDetail";
import LearningProgressLearnerEdit from "@/pages/learning-progress/LearningProgressLearnerEdit";
import LearningProgressReport from "@/pages/learning-progress/LearningProgressReport";
import LearningProgressReportCompany from "@/pages/learning-progress/LearningProgressReportCompany";
import { LearnerLayout } from "@/features/learner/layout/LearnerLayout";
import LearnerList from "@/pages/learner/LearnerList";
import LearnerCreate from "@/pages/learner/LearnerCreate";
import LearnerBulkImport from "@/pages/learner/LearnerBulkImport";
import LearnerBulkImportComplete from "@/pages/learner/LearnerBulkImportComplete";
import LearnerDetail from "@/pages/learner/LearnerDetail";
import LearnerEdit from "@/pages/learner/LearnerEdit";
import LearnerPlanList from "@/pages/learner/LearnerPlanList";
import CompanyUserCreatePage from "@/pages/company/CompanyUserCreate";
import CompanyUserEditPage from "@/pages/company/CompanyUserEdit";
import Rso from "@/pages/rso/Rso";
import RsoEdit from "@/pages/rso/RsoEdit";
import { RsoLayout } from "@/features/rso/layout/RsoLayout";

export const routes: RouteObject[] = [
  {
    element: <LoginLayout />,
    children: [
      {
        path: AppRoutes.recover,
        element: <Recover />,
      },
      {
        path: AppRoutes.recoverComplete,
        element: <RecoverComplete />,
      },
      {
        path: AppRoutes.resetPassword,
        element: <ResetPassword />,
      },
      {
        path: AppRoutes.resetPasswordComplete,
        element: <ResetPasswordComplete />,
      },
      {
        path: AppRoutes.register,
        element: <Register />,
      },
      {
        path: AppRoutes.registerComplete,
        element: <RegisterComplete />,
      },
      {
        path: AppRoutes.login,
        element: <Login />,
      },
      {
        path: AppRoutes.confirmEmail,
        element: <ConfirmEmail />,
      },
    ],
  },
  {
    element: <MatchLayoutWithAuth />,
    children: [
      {
        path: AppRoutes.home,
        element: <Dashboard />,
        id: AppRouteNames.home.value,
      },
      { path: AppRoutes.information, element: <InformationList />, id: AppRouteNames.information.value },
      { path: AppRoutes.informationDetail, element: <InformationDetail />, id: AppRouteNames.informationDetail.value },
      {
        path: AppRoutes.talentList,
        element: <TalentList />,
        id: AppRouteNames.talentList.value,
      },
      {
        element: <TalentLayout />,
        children: [
          { path: AppRoutes.talentCreate, element: <TalentCreatePage />, id: AppRouteNames.talentCreate.value },
          { path: AppRoutes.talentEdit, element: <TalentEditPage />, id: AppRouteNames.talentEdit.value },
          { path: AppRoutes.talentDetail, element: <TalentDetailPage />, id: AppRouteNames.talentDetail.value },
        ],
      },
      {
        children: [{ path: AppRoutes.talentImport, element: <TalentImport />, id: AppRouteNames.talentImport.value }],
      },
      { path: AppRoutes.companyList, element: <CompanyList />, id: AppRouteNames.companyList.value },
      {
        element: <CompanyLayout />,
        children: [
          { path: AppRoutes.companyCreate, element: <CompanyCreatePage />, id: AppRouteNames.companyCreate.value },
          { path: AppRoutes.companyEdit, element: <CompanyEditPage />, id: AppRouteNames.companyEdit.value },
          { path: AppRoutes.companyDetail, element: <CompanyDetailPage />, id: AppRouteNames.companyDetail.value },
          { path: AppRoutes.companyImport, element: <CompanyImport />, id: AppRouteNames.companyImport.value },
          { path: AppRoutes.companyUserCreate, element: <CompanyUserCreatePage />, id: AppRouteNames.companyUserCreate.value },
          { path: AppRoutes.companyUserEdit, element: <CompanyUserEditPage />, id: AppRouteNames.companyUserEdit.value },
        ],
      },
      { path: AppRoutes.caseList, element: <CaseList />, id: AppRouteNames.caseList.value },
      {
        element: <CaseLayout />,
        children: [
          { path: AppRoutes.caseCreate, element: <CaseCreate />, id: AppRouteNames.caseCreate.value },
          { path: AppRoutes.caseEdit, element: <CaseEdit />, id: AppRouteNames.caseEdit.value },
        ],
      },
      {
        element: <UserSettingLayout />,
        children: [
          { path: AppRoutes.userSettingDetail, element: <UserSettingDetail />, id: AppRouteNames.userSettingDetail.value },
          { path: AppRoutes.userSettingEdit, element: <UserSettingEdit />, id: AppRouteNames.userSettingEdit.value },
          { path: AppRoutes.userSettingChangeEmail, element: <UserSettingChangeEmail />, id: AppRouteNames.userSettingChangeEmail.value },
        ],
      },
      {
        element: <MatchLayout />,
        children: [
          { path: AppRoutes.match, element: <Match />, id: AppRouteNames.match.value },
          { path: AppRoutes.matchEdit, element: <MatchEdit />, id: AppRouteNames.matchEdit.value },
        ],
      },
      { path: AppRoutes.jobList, element: <JobList />, id: AppRouteNames.jobList.value },
      {
        element: <JobLayout />,
        children: [
          { path: AppRoutes.jobCreate, element: <JobCreatePage />, id: AppRouteNames.jobCreate.value },
          { path: AppRoutes.jobDetail, element: <JobDetailPage />, id: AppRouteNames.jobDetail.value },
          { path: AppRoutes.jobEdit, element: <JobEditPage />, id: AppRouteNames.jobEdit.value },
        ],
      },
      { path: AppRoutes.learningProgress, element: <LearningProgress />, id: AppRouteNames.learningProgress.value },
      { path: AppRoutes.learningProgressDetail, element: <LearningProgressDetail />, id: AppRouteNames.learningProgressDetail.value },
      { path: AppRoutes.learningProgressLearnerDetail, element: <LearningProgressLearnerDetail />, id: AppRouteNames.learningProgressLearnerDetail.value },
      { path: AppRoutes.learningProgressLearnerEdit, element: <LearningProgressLearnerEdit />, id: AppRouteNames.learningProgressLearnerEdit.value },
      { path: AppRoutes.learningProgressReport, element: <LearningProgressReport />, id: AppRouteNames.learningProgressReport.value },
      { path: AppRoutes.learningProgressReportCompany, element: <LearningProgressReportCompany />, id: AppRouteNames.learningProgressReportCompany.value },
      { path: AppRoutes.learnerList, element: <LearnerList />, id: AppRouteNames.learnerList.value },
      { path: AppRoutes.learnerBulkImport, element: <LearnerBulkImport />, id: AppRouteNames.learnerBulkImport.value },
      { path: AppRoutes.learnerBulkImportComplete, element: <LearnerBulkImportComplete />, id: AppRouteNames.learnerBulkImportComplete.value },
      {
        element: <LearnerLayout />,
        children: [
          { path: AppRoutes.learnerCreate, element: <LearnerCreate />, id: AppRouteNames.learnerCreate.value },
          { path: AppRoutes.learnerDetail, element: <LearnerDetail />, id: AppRouteNames.learnerDetail.value },
          { path: AppRoutes.learnerEdit, element: <LearnerEdit />, id: AppRouteNames.learnerEdit.value },
        ],
      },
      { path: AppRoutes.learnerPlanList, element: <LearnerPlanList />, id: AppRouteNames.learnerPlanList.value },
      {
        element: <RsoLayout />,
        children: [
          { path: AppRoutes.rsoDetail, element: <Rso />, id: AppRouteNames.rsoDetail.value },
          { path: AppRoutes.rsoEdit, element: <RsoEdit />, id: AppRouteNames.rsoEdit.value },
        ]
      },
      { path: AppRoutes.examSteps, element: <ExamStepsList />, id: AppRouteNames.examSteps.value },
      { path: AppRoutes.examStepDetail, element: <ExamStepDetail />, id: AppRouteNames.examStepDetail.value },
    ],
  },
  {
    path: AppRoutes.logout,
    element: <Logout />,
  },
  { path: AppRoutes.serverErrorPage, element: <ServerErrorPage /> },
  { path: AppRoutes.maintenance, element: <MaintenancePage /> },
  { path: "*", element: <PageNotFound /> },
];

const flattenRoutes = (routes: RouteObject[]): RouteObject[] => {
  const flatRoutes: RouteObject[] = [];

  const extractChildren = (route: RouteObject) => {
    if (route.children) {
      route.children.forEach((child) => {
        extractChildren(child); // Recursively extract children
      });
    } else {
      flatRoutes.push(route); // Push the route if there are no children
    }
  };

  routes.forEach((route) => extractChildren(route));

  return flatRoutes;
};

export const flattenedRoutes = flattenRoutes(routes);
